import type { LicenseBalance } from '../api/licensing';
import type { Migration, MigrationDevice } from '../api/migrations';
import type { TenantDetail } from '../api/tenantRegistry';
import type { MintReadiness } from '../api/waves';

// The seven pre-activation checks mockup 4 draws (FD-490), as a pure function.
//
// ── NOTHING HERE GATES ACTIVATION, AND THE LIB MUST NOT IMPLY THAT IT DOES ──
//
// `manager/get-mint-readiness/run.ps1` says it in its own header, about the one
// input here that is a live tenant probe:
//
//     "It never gates activation. FD-ADR-023 §4 and FD-ADR-029 §3 both settled
//      that tenant state is an operator action in someone else's tenant and must
//      not block the console; and this probe is OPTIMISTIC BY DESIGN (see
//      `caveats`), so blocking on it would trade a named error for a false
//      block."
//
// That reasoning covers every check on this list, not just the minting one.
// Consent is granted by an admin in a tenant Rubix does not own, on a schedule
// nobody here controls; a licence balance is ATC's answer; a manifest arrives
// when a device boots. `activate-wave` still refuses on its OWN terms with its
// OWN named codes (`409 LICENSE_POOL_EXHAUSTED`, `409 WAVE_ACTIVE`, an
// `AADSTS…` from DRS), and those refusals are the control. This is a briefing.
//
// ── A MISSING INPUT IS `unknown`, NEVER `fail` ───────────────────────────────
//
// The distinction is the whole point of the five-state vocabulary. "We could not
// tell" and "we checked and it is wrong" demand opposite operator actions, and
// collapsing them is the FD-269 class of defect — a field reporting a state it
// cannot know. It is also the FD-246 discipline applied to a checklist: an
// absent value is its own answer, not a quiet red.
//
// ── WHAT THIS MODULE DELIBERATELY DOES NOT SAY ───────────────────────────────
//
// It never promises that a credit shortfall proceeds. `api/licensing.ts` states
// the constraint: `409 LICENSE_CREDITS_EXHAUSTED` refuses only where a
// deployment has set `LICENSING_ENFORCE_CONSUMABLE=true`, which ships `false`
// (FD-ADR-041 decision 7) — and the console cannot read that setting, so no copy
// here may make the promise in either direction.
//
// And it never says activation locks campaign membership. Mockup 4 builds a
// whole panel on that claim; R-FD-051 D1 refuses it, because `add-devices` has
// no `BprtStatus` guard by design (FD-ADR-041 §5), FD-ADR-035 §3 makes remove+add
// a failed device's only retry path, and FD-ADR-036's freeze is additive-only.

export type PreflightState = 'pass' | 'warn' | 'fail' | 'not-applicable' | 'unknown';

export type PreflightCheckId =
  | 'source-tenant'
  | 'destination-tenant'
  | 'devices-assigned'
  | 'manifests-received'
  | 'identity-reconciliation'
  | 'license-available'
  | 'minting-account';

export interface PreflightCheck {
  id: PreflightCheckId;
  /** The row heading, as mockup 4 spells it. */
  label: string;
  state: PreflightState;
  /** One sentence naming the fact behind the state. Always present. */
  detail: string;
  /** The short pill at the right of the row. */
  badge: string;
}

export interface CampaignPreflight {
  /** Always seven rows, in mockup order. A check never disappears. */
  checks: PreflightCheck[];
  /**
   * `passed of applicable`, which is what the mockup's "7 of 7 checks passed"
   * renders.
   *
   * `not-applicable` is excluded from BOTH numbers, so a Hybrid campaign — which
   * has no source tenant at all — reads "6 of 6" rather than a permanent "6 of
   * 7" that looks like a check nobody ever fixed.
   */
  passed: number;
  applicable: number;
  /** Checks the console could not evaluate. Reported apart from failures. */
  unknownCount: number;
}

export interface PreflightInputs {
  migration: Pick<
    Migration,
    | 'migrationType'
    | 'deviceCount'
    | 'sourceTenantId'
    | 'sourceTenantDomain'
    | 'destTenantId'
    | 'destTenantDomain'
    | 'readinessSummary'
  >;
  devices: Pick<MigrationDevice, 'identityStatus'>[];
  /** `null` when licensing is off in this deployment, or the read failed. */
  licenseBalance?: LicenseBalance | null;
  mintReadiness?: MintReadiness | null;
  sourceTenant?: TenantDetail | null;
  destTenant?: TenantDetail | null;
}

const LABELS: Record<PreflightCheckId, string> = {
  'source-tenant': 'Source tenant connected',
  'destination-tenant': 'Destination tenant connected',
  'devices-assigned': 'Devices assigned',
  'manifests-received': 'Device manifests received',
  'identity-reconciliation': 'Identity reconciliation',
  'license-available': 'Migration licence available',
  'minting-account': 'Minting account validated',
};

const ORDER: PreflightCheckId[] = [
  'source-tenant',
  'destination-tenant',
  'devices-assigned',
  'manifests-received',
  'identity-reconciliation',
  'license-available',
  'minting-account',
];

export function deriveCampaignPreflight(inputs: PreflightInputs): CampaignPreflight {
  const built: Record<PreflightCheckId, Omit<PreflightCheck, 'id' | 'label'>> = {
    'source-tenant': sourceTenantCheck(inputs),
    'destination-tenant': destinationTenantCheck(inputs),
    'devices-assigned': devicesAssignedCheck(inputs),
    'manifests-received': manifestsCheck(inputs),
    'identity-reconciliation': identityCheck(inputs),
    'license-available': licenceCheck(inputs),
    'minting-account': mintingCheck(inputs),
  };

  const checks = ORDER.map((id) => ({ id, label: LABELS[id], ...built[id] }));
  const applicable = checks.filter((c) => c.state !== 'not-applicable');

  return {
    checks,
    passed: applicable.filter((c) => c.state === 'pass').length,
    applicable: applicable.length,
    unknownCount: applicable.filter((c) => c.state === 'unknown').length,
  };
}

type Outcome = Omit<PreflightCheck, 'id' | 'label'>;

function tenantLabel(domain: string | null | undefined, id: string | null | undefined): string {
  const d = (domain ?? '').trim();
  if (d) return d;
  const i = (id ?? '').trim();
  return i || 'the tenant';
}

/**
 * Map a registry consent verdict onto a check state.
 *
 * `insufficient` is `warn`, NOT `fail`, and that is the honest reading rather
 * than a softened one: the remedy is an admin consent run in a tenant this
 * deployment does not own, `activate-wave` does not consult it, and painting it
 * red on a page whose button says "Activate wave" would read as a block the
 * platform does not impose.
 *
 * `unknown` stays `unknown`. `api/tenantRegistry.ts` states the rule in terms —
 * the Graph read FAILED, which is "couldn't verify" and is NEVER the same as
 * "some roles are missing". Collapsing the two is the one thing that type's doc
 * comment forbids.
 */
function consentOutcome(tenant: TenantDetail, name: string): Outcome {
  switch (tenant.consentStatus) {
    case 'consented':
      return {
        state: 'pass',
        detail: `${name} is connected and the tenant app is present.`,
        badge: 'Connected',
      };
    case 'insufficient':
      return {
        state: 'warn',
        detail:
          tenant.missingRoles.length > 0
            ? `${name} is missing ${tenant.missingRoles.length} Graph role${
                tenant.missingRoles.length === 1 ? '' : 's'
              }: ${tenant.missingRoles.join(', ')}.`
            : `${name} has not granted every Graph role the broker needs.`,
        badge: 'Needs consent',
      };
    default:
      return {
        state: 'unknown',
        detail: `${name}'s consent state could not be verified.`,
        badge: 'Not verified',
      };
  }
}

/**
 * `not-applicable` ON D2C — never `fail`, and never a silently-passing row.
 *
 * A Hybrid migration has no source tenant: the source identities are on-prem AD
 * users, and `Resolve-MigrationTenant` answers a request for one with
 * `400 D2C_HAS_NO_SOURCE_TENANT`. Marking the check failed would invent a
 * prerequisite that cannot exist; marking it passed would claim a connection
 * that was never made.
 */
function sourceTenantCheck({ migration, sourceTenant }: PreflightInputs): Outcome {
  if (migration.migrationType === 'D2C') {
    return {
      state: 'not-applicable',
      detail: 'Hybrid migrations have no source tenant — source identities are on-prem AD users.',
      badge: 'Not applicable',
    };
  }

  const name = tenantLabel(migration.sourceTenantDomain, migration.sourceTenantId);
  if (!(migration.sourceTenantId ?? '').trim()) {
    return {
      state: 'warn',
      detail: 'No source tenant is recorded on this campaign.',
      badge: 'Not recorded',
    };
  }
  if (!sourceTenant) {
    return {
      state: 'unknown',
      detail: `${name} has not been read from the tenant registry.`,
      badge: 'Not checked',
    };
  }
  return consentOutcome(sourceTenant, name);
}

/**
 * Both migration types have a destination, so this check is never
 * `not-applicable` — but a D2C campaign may legitimately carry NO
 * `destTenantId`, because FD-ADR-029 kept the deployment tenant as the
 * default and made an explicit destination the override. That is an ordinary,
 * zero-config campaign and must not read as a fault; the console simply cannot
 * name the tenant from the row, so it says so.
 */
function destinationTenantCheck({ migration, destTenant }: PreflightInputs): Outcome {
  const recorded = (migration.destTenantId ?? '').trim();
  const name = tenantLabel(migration.destTenantDomain, migration.destTenantId);

  if (!recorded) {
    if (migration.migrationType === 'D2C') {
      return {
        state: 'unknown',
        detail:
          'No destination tenant is recorded, so this campaign uses the deployment tenant; its consent state was not read.',
        badge: 'Deployment default',
      };
    }
    return {
      state: 'warn',
      detail: 'No destination tenant is recorded on this campaign.',
      badge: 'Not recorded',
    };
  }

  if (!destTenant) {
    return {
      state: 'unknown',
      detail: `${name} has not been read from the tenant registry.`,
      badge: 'Not checked',
    };
  }
  return consentOutcome(destTenant, name);
}

/**
 * The only check that reads `fail`, and it earns it: the fact is LOCAL, it is
 * certain, and a wave with no devices migrates nothing and reserves nothing.
 *
 * `fail` still does not mean blocked — see the module header. It means the
 * console checked and the answer is no, which is a different sentence from the
 * `warn`s above (someone else's tenant said no) and the `unknown`s (nobody
 * answered).
 */
function devicesAssignedCheck({ migration }: PreflightInputs): Outcome {
  const count = migration.deviceCount ?? 0;
  if (count <= 0) {
    return {
      state: 'fail',
      detail: 'No devices are assigned to this campaign.',
      badge: 'None',
    };
  }
  return {
    state: 'pass',
    detail: `${count} device${count === 1 ? ' is' : 's are'} assigned to this campaign.`,
    badge: `${count} device${count === 1 ? '' : 's'}`,
  };
}

/**
 * `notEvaluated === 0` over ZERO DEVICES IS VACUOUSLY TRUE, and rendering that
 * as a pass would be the FD-191 empty-partition defect on a checklist: "all 0
 * devices have checked in" is not a fact anyone asked for. An empty campaign
 * reports `unknown` and points at the check above it, which is where the real
 * answer is.
 */
function manifestsCheck({ migration }: PreflightInputs): Outcome {
  const count = migration.deviceCount ?? 0;
  const summary = migration.readinessSummary;

  if (count <= 0) {
    return {
      state: 'unknown',
      detail: 'No devices are assigned, so there is nothing to check in yet.',
      badge: 'No devices',
    };
  }
  if (!summary?.byStatus) {
    return {
      state: 'unknown',
      detail: 'This deployment did not report a readiness summary for the campaign.',
      badge: 'Not reported',
    };
  }

  const notEvaluated = summary.byStatus.notEvaluated ?? 0;
  if (notEvaluated > 0) {
    return {
      state: 'warn',
      detail: `${notEvaluated} of ${count} devices have not checked in and submitted a manifest yet.`,
      badge: `${notEvaluated} pending`,
    };
  }
  return {
    state: 'pass',
    detail:
      count === 1
        ? 'The assigned device has checked in and submitted a manifest.'
        : `All ${count} assigned devices have checked in and submitted a manifest.`,
    badge: 'Complete',
  };
}

/**
 * Identity reconciliation, which has to keep FOUR answers apart.
 *
 * `identityStatus` is a three-value field plus absence (FD-357 / FD-ADR-036 §4),
 * and the type's own comment calls out the trap: `n/a` — no `primaryUserSid`
 * yet, so nothing to map — is "an ordinary state, never a warning colour". A
 * check that counted `ready` against the total would paint every pre-manifest
 * campaign amber for a condition that resolves itself when the device boots.
 *
 * So the number that matters is `not-ready`: a device whose delivery WOULD be
 * held at `409 IDENTITY_NOT_READY`. Everything else is either mapped or not yet
 * asked.
 */
function identityCheck({ migration, devices }: PreflightInputs): Outcome {
  const count = migration.deviceCount ?? 0;
  if (count <= 0) {
    return {
      state: 'unknown',
      detail: 'No devices are assigned, so there are no identities to reconcile.',
      badge: 'No devices',
    };
  }
  if (devices.length === 0) {
    return {
      state: 'unknown',
      detail: 'Device rows were not loaded, so identity readiness could not be evaluated.',
      badge: 'Not checked',
    };
  }
  if (devices.every((d) => d.identityStatus === undefined || d.identityStatus === null)) {
    return {
      state: 'unknown',
      detail: 'This deployment does not report per-device identity readiness.',
      badge: 'Not reported',
    };
  }

  const notReady = devices.filter((d) => d.identityStatus === 'not-ready').length;
  if (notReady > 0) {
    return {
      state: 'warn',
      detail: `${notReady} of ${devices.length} devices have no usable identity mapping and would be held at delivery.`,
      badge: `${notReady} unmapped`,
    };
  }

  const ready = devices.filter((d) => d.identityStatus === 'ready').length;
  const pending = devices.filter((d) => d.identityStatus === 'n/a').length;
  return {
    state: 'pass',
    detail:
      pending > 0
        ? `${ready} of ${devices.length} devices have an identity mapping; ${pending} have not submitted a manifest yet.`
        : `${ready} of ${devices.length} devices have an identity mapping.`,
    badge: 'Complete',
  };
}

/**
 * Licence availability, reported against the bucket that ACTUALLY refuses.
 *
 * FD-ADR-041 changed the unit from the campaign to the device: activation
 * reserves one unit per assigned device, so the number required is the device
 * count and not 1. Two buckets can answer, and they do not carry the same
 * weight:
 *
 *   * the POOL hard-blocks on every deployment — `409 LICENSE_POOL_EXHAUSTED`
 *     fires on `poolAvailable < N` (FD-ADR-041 decision 7), so a shortfall there
 *     is a `fail`, and the detail names the code an operator will actually see;
 *   * the CONSUMABLE bucket refuses only under `LICENSING_ENFORCE_CONSUMABLE`,
 *     which ships `false` and which the console cannot read — so a shortfall is
 *     a `warn`, and the copy promises nothing in either direction.
 *
 * `available` MAY BE NEGATIVE and is passed through unclamped, for the reason
 * `api/licensing.ts` gives: the 1:1 bucket is advisory, an account can
 * legitimately be over-drawn, and clamping would hide exactly the fact the
 * operator opened the page to see.
 */
function licenceCheck({ migration, licenseBalance }: PreflightInputs): Outcome {
  const required = migration.deviceCount ?? 0;

  if (!licenseBalance) {
    return {
      state: 'unknown',
      detail: 'The licence balance was not read, so availability is unknown.',
      badge: 'Not checked',
    };
  }
  if (required <= 0) {
    return {
      state: 'unknown',
      detail: 'No devices are assigned, so no licences are required yet.',
      badge: 'No devices',
    };
  }

  const pool = licenseBalance.pool;
  if (pool && typeof pool.available === 'number') {
    if (pool.available < required) {
      return {
        state: 'fail',
        detail: `${pool.available} pool slot${
          pool.available === 1 ? '' : 's'
        } available and ${required} required — activation is refused with LICENSE_POOL_EXHAUSTED.`,
        badge: 'Exhausted',
      };
    }
    return {
      state: 'pass',
      detail: `${pool.available} pool slots available (${required} required).`,
      badge: 'Available',
    };
  }

  const consumable = licenseBalance.consumable;
  if (consumable && typeof consumable.available === 'number') {
    if (consumable.available < required) {
      return {
        state: 'warn',
        detail: `${consumable.available} licence${
          consumable.available === 1 ? '' : 's'
        } available and ${required} required.`,
        badge: 'Shortfall',
      };
    }
    return {
      state: 'pass',
      detail: `${consumable.available} licences available (${required} required).`,
      badge: 'Available',
    };
  }

  return {
    state: 'unknown',
    detail: 'The licence balance carried no available count for either bucket.',
    badge: 'Not reported',
  };
}

/**
 * Minting-account readiness, which is the check whose producer most insists it
 * is a briefing rather than a gate.
 *
 * Three shapes of answer, kept apart:
 *
 *   * `checked: false` is NOT a failure — the endpoint's own type says so, and
 *     `checkReason` carries why the probe did not run. `unknown`.
 *   * a NEGATIVE signal (no account recorded, the account is absent, its CA
 *     exclusion is unhealthy) is `warn` — a real operator action, in a tenant
 *     this deployment does not own.
 *   * a CA exclusion the probe COULD NOT VERIFY (`caExclusionHealthy: null`, or
 *     absent from an older Manager) is `unknown`, never `warn` and never `fail`
 *     (FD-609). "We could not tell" is a different operator question from "a
 *     policy may block this", and collapsing the two is exactly the FD-490
 *     defect FD-609 closed on the Manager side. Still advisory: nothing here
 *     gates the Activate button.
 *
 * A `pass` still names the `caveats` count. The FD-451 contract ships them on
 * the wire beside the verdict precisely because "a panel that renders as a green
 * light it cannot justify is worse than no panel."
 */
function mintingCheck({ mintReadiness }: PreflightInputs): Outcome {
  if (!mintReadiness) {
    return {
      state: 'unknown',
      detail: 'Minting-account readiness was not read.',
      badge: 'Not checked',
    };
  }

  const tenant = tenantLabel(mintReadiness.tenantDomain, mintReadiness.tenantId);
  const upn = (mintReadiness.serviceAccountUpn ?? '').trim();

  if (!upn || mintReadiness.serviceAccountSource === 'none') {
    return {
      state: 'warn',
      detail: `No minting account is recorded for ${tenant}.`,
      badge: 'Not configured',
    };
  }
  if (!mintReadiness.checked) {
    return {
      state: 'unknown',
      detail:
        mintReadiness.checkReason?.trim() ||
        `${upn} is recorded, but the live probe did not run.`,
      badge: 'Not checked',
    };
  }

  const account = mintReadiness.account;
  if (!account) {
    return {
      state: 'unknown',
      detail: `${upn} is recorded, but the probe returned no account detail.`,
      badge: 'Not reported',
    };
  }
  if (!account.present) {
    return {
      state: 'warn',
      detail: `${upn} was not found in ${tenant}.`,
      badge: 'Not found',
    };
  }
  if (account.caExclusionHealthy === null || account.caExclusionHealthy === undefined) {
    return {
      state: 'unknown',
      detail: `${upn} is present, but the probe could not verify the Conditional Access exclusion.`,
      badge: 'Not verified',
    };
  }
  if (account.caExclusionHealthy === false) {
    return {
      state: 'warn',
      detail: `${upn} is present but its Conditional Access exclusion is not healthy.`,
      badge: 'CA exclusion',
    };
  }

  const caveats = mintReadiness.caveats?.length ?? 0;
  return {
    state: 'pass',
    detail:
      caveats > 0
        ? `${upn} is configured and can sign in (${caveats} caveat${caveats === 1 ? '' : 's'} reported).`
        : `${upn} is configured and can sign in.`,
    badge: 'Ready',
  };
}
